import React, { useEffect, ReactNode } from 'react'
import { SnackbarProvider, useSnackbar } from 'notistack'
import { Alert } from '@mui/material'
import type { CustomContentProps } from 'notistack'
import { notificationService } from '@/services/notificationService'

/** Thời gian hiển thị snackbar mặc định (ms) — áp dụng chung success/error/warning/info */
export const DEFAULT_SNACKBAR_DURATION = 2500
/** Lỗi/cảnh báo cần đọc kỹ hơn → hiển thị lâu hơn mặc định. */
export const ERROR_SNACKBAR_DURATION = 5000

const variantToSeverity: Record<string, 'error' | 'success' | 'warning' | 'info'> = {
  default: 'info',
  error: 'error',
  success: 'success',
  warning: 'warning',
  info: 'info',
}

const AlertSnackbar = React.forwardRef<HTMLDivElement, CustomContentProps>((props, ref) => {
  const { message, variant, style, id, action } = props
  const { closeSnackbar } = useSnackbar()
  const severity = variantToSeverity[variant] ?? 'info'
  const resolvedAction = typeof action === 'function' ? action(id) : action
  const hasCustomTone = Boolean(style && (style as React.CSSProperties).backgroundColor)
  return (
    <Alert
      ref={ref}
      severity={severity}
      variant={hasCustomTone ? 'standard' : 'filled'}
      style={style}
      sx={{
        width: '100%',
        alignItems: 'flex-start',
        boxShadow: hasCustomTone ? '0 4px 16px rgba(0,0,0,0.12)' : undefined,
        ...(hasCustomTone
          ? {
              bgcolor: (style as React.CSSProperties).backgroundColor,
              color: (style as React.CSSProperties).color ?? '#1C1E21',
              borderLeft: (style as React.CSSProperties).borderLeft,
              '& .MuiAlert-icon': {
                color: 'var(--tb-accent)',
                pt: '3px',
              },
              '& .MuiAlert-action .MuiIconButton-root': {
                color: 'rgba(28, 30, 33, 0.55)',
              },
            }
          : {
              '& .MuiAlert-icon': {
                pt: '3px',
              },
            }),
        '& .MuiAlert-message': {
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          overflowWrap: 'anywhere',
          lineHeight: 1.45,
        },
      }}
      action={resolvedAction}
      onClose={() => closeSnackbar(id)}
    >
      {(() => {
        const raw = message == null ? '' : typeof message === 'string' ? message : String(message)
        const text = raw.trim()
        if (!text || text === 'undefined' || text === '[object Object]') {
          return severity === 'error' ? 'Đã xảy ra lỗi' : ''
        }
        return text
      })()}
    </Alert>
  )
})
AlertSnackbar.displayName = 'AlertSnackbar'

const SnackbarComponents = {
  default: AlertSnackbar,
  error: AlertSnackbar,
  success: AlertSnackbar,
  warning: AlertSnackbar,
  info: AlertSnackbar,
}

interface NotificationSetupProps {
  children: ReactNode
}

const NotificationSetup: React.FC<NotificationSetupProps> = ({ children }) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()

  useEffect(() => {
    notificationService.setHandler((message, severity = 'info', options) => {
      const snackbarOptions = {
        variant: severity,
        autoHideDuration:
          options?.duration ??
          (severity === 'error' || severity === 'warning' ? ERROR_SNACKBAR_DURATION : DEFAULT_SNACKBAR_DURATION),
        anchorOrigin: options?.position || {
          vertical: 'top' as const,
          horizontal: 'right' as const,
        },
        style: options?.style,
        preventDuplicate: options?.preventDuplicate ?? true,
        persist: options?.persist || false,
        action: options?.action
          ? (key: string | number) => (
              <button
                onClick={() => {
                  options.action?.onClick()
                  closeSnackbar(key)
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'inherit',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                {options.action?.label}
              </button>
            )
          : undefined,
      }

      enqueueSnackbar(message, snackbarOptions)
    })

    return () => {
      notificationService.clearHandler()
    }
  }, [enqueueSnackbar, closeSnackbar])

  return <>{children}</>
}

interface NotificationProviderProps {
  children: ReactNode
  maxSnack?: number
  anchorOrigin?: {
    vertical: 'top' | 'bottom'
    horizontal: 'left' | 'center' | 'right'
  }
  dense?: boolean
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
  maxSnack = 3,
  anchorOrigin = { vertical: 'top', horizontal: 'right' },
  dense = true,
}) => {
  return (
    <SnackbarProvider
      maxSnack={maxSnack}
      anchorOrigin={anchorOrigin}
      dense={dense}
      Components={SnackbarComponents}
    >
      <NotificationSetup>{children}</NotificationSetup>
    </SnackbarProvider>
  )
}

export default NotificationProvider
